import { useEffect, useState } from 'react';
import type { FeedbackDiagnostics, FeedbackItem, FeedbackList, FeedbackPriority, FeedbackStatus } from '../../../shared/protocol.ts';
import { account } from '../lib/account.ts';
import { api } from '../lib/api.ts';
import { AppBar, ConfirmDialog, Icon, useObservable, useTitle } from './common.tsx';
import { FEEDBACK_CATEGORIES } from './Feedback.tsx';

const STATUSES: [FeedbackStatus, string][] = [
  ['new', 'New'],
  ['reviewing', 'Reviewing'],
  ['action', 'Action'],
  ['done', 'Done'],
  ['wontfix', 'Won’t fix'],
  ['duplicate', 'Duplicate'],
];
const PRIORITIES: FeedbackPriority[] = ['high', 'medium', 'low'];
const CATEGORY_TONE: Record<string, string> = { problem: 'bad', idea: 'accent', praise: 'ok' };

/** Everything sent with the Feedback button (operators only): triage by status and priority, with notes. */
export function AdminFeedbackPage() {
  useObservable(account);
  useTitle('Feedback');
  const me = account.account;
  const [status, setStatus] = useState<FeedbackStatus | null>(() => new URLSearchParams(location.search).get('status') as FeedbackStatus | null);
  const [data, setData] = useState<FeedbackList | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!me?.isOperator) return;
    api.feedback(status).then(
      (d) => (setData(d), setError('')),
      (e) => setError((e as Error).message),
    );
  }, [me?.id, status]);
  // Links in the emails point at one item.
  const loaded = !!data;
  useEffect(() => {
    if (loaded && location.hash) document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [loaded]);

  const filter = (s: FeedbackStatus | null) => {
    setStatus(s);
    history.replaceState(null, '', s ? `?status=${s}` : location.pathname);
  };
  const replace = (f: FeedbackItem, was?: FeedbackStatus) =>
    setData((d) => {
      if (!d) return d;
      const counts = { ...d.counts };
      if (was && was !== f.status) {
        counts[was] = (counts[was] ?? 1) - 1;
        counts[f.status] = (counts[f.status] ?? 0) + 1;
      }
      return { items: d.items.map((x) => (x.id === f.id ? f : x)), counts };
    });
  const remove = (f: FeedbackItem) =>
    setData((d) => d && { items: d.items.filter((x) => x.id !== f.id), counts: { ...d.counts, [f.status]: (d.counts[f.status] ?? 1) - 1 } });

  if (!account.checked || (me?.isOperator && !data && !error)) {
    return (
      <div className="page">
        <AppBar />
        <main className="feedback-page">
          <div className="card row muted">
            <span className="spinner" /> Loading…
          </div>
        </main>
      </div>
    );
  }
  if (!me?.isOperator) {
    return (
      <div className="page">
        <AppBar />
        <main className="feedback-page">
          <div className="card">
            <h2>Only the people who run this site can see feedback</h2>
            <p className="muted">{me ? `You’re signed in as ${me.email}.` : 'Sign in with an operator’s email first.'}</p>
            <a className="btn primary" href="/" style={{ alignSelf: 'flex-start' }}>
              {me ? 'Your studios' : 'Sign in'}
            </a>
          </div>
        </main>
      </div>
    );
  }

  const total = Object.values(data?.counts ?? {}).reduce((a, n) => a + n, 0);
  return (
    <div className="page">
      <AppBar />
      <main className="feedback-page">
        <div className="col" style={{ gap: 4 }}>
          <span className="eyebrow">Operators</span>
          <h1>Feedback</h1>
          <span className="muted small">Mark items Action for Claude’s /process-feedback skill to work through. Sorted newest first.</span>
        </div>
        <div className="option-chips" role="radiogroup" aria-label="Show">
          <button role="radio" aria-checked={!status} className={!status ? 'on' : ''} onClick={() => filter(null)}>
            All · {total}
          </button>
          {STATUSES.map(([id, label]) => (
            <button key={id} role="radio" aria-checked={status === id} className={status === id ? 'on' : ''} onClick={() => filter(id)}>
              {label} · {data?.counts[id] ?? 0}
            </button>
          ))}
        </div>
        {error && (
          <div className="alert bad">
            <Icon name="warn" size={16} /> {error}
          </div>
        )}
        {data && !data.items.length && (
          <div className="card empty" style={{ border: '1px solid var(--line)' }}>
            <Icon name="chat" size={26} />
            <strong style={{ color: 'var(--text)' }}>{status ? 'Nothing here' : 'No feedback yet'}</strong>
            Everything sent with the Feedback button appears here, and you’re emailed as it arrives.
          </div>
        )}
        {data?.items.map((f) => (
          <FeedbackCard key={f.id} f={f} onChange={(g) => replace(g, f.status)} onDelete={() => remove(f)} />
        ))}
      </main>
    </div>
  );
}

function FeedbackCard({ f, onChange, onDelete }: { f: FeedbackItem; onChange: (f: FeedbackItem) => void; onDelete: () => void }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [diagnostics, setDiagnostics] = useState<FeedbackDiagnostics | null>(null);
  const [confirming, setConfirming] = useState(false);
  const run = async (p: Promise<FeedbackItem>) => {
    setBusy(true);
    setError('');
    try {
      onChange(await p);
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };
  const addNote = async () => {
    if (note.trim() && (await run(api.commentOnFeedback(f.id, note.trim())))) setNote('');
  };
  const category = FEEDBACK_CATEGORIES.find(([id]) => id === f.category)?.[1] ?? f.category;
  const who = f.name || f.email || 'Someone not signed in';
  return (
    <section className={`card feedback-card ${location.hash === `#${f.id}` ? 'linked' : ''}`} id={f.id}>
      <div className="feedback-head">
        <span className={`pill ${CATEGORY_TONE[f.category] ?? ''}`}>{category}</span>
        <strong className="ellipsis">{who}</strong>
        {f.name && f.email && <span className="muted small ellipsis">{f.email}</span>}
        {!f.signedIn && f.email && <span className="faint small">not verified</span>}
        <span className="grow" />
        <span className="faint small" title={new Date(f.createdAt).toLocaleString()}>
          {when(f.createdAt)}
        </span>
      </div>
      <p className="feedback-message">{f.message}</p>
      <div className="feedback-meta">
        <span>
          From <span className="mono">{f.pageUrl}</span>
        </span>
        {f.studioId && <span>Studio {f.studioId}</span>}
        {f.userAgent && <span>{browserOf(f.userAgent)}</span>}
      </div>
      {f.hasDiagnostics && (
        <details
          onToggle={(e) => {
            if ((e.target as HTMLDetailsElement).open && !diagnostics) api.feedbackItem(f.id).then((x) => setDiagnostics(x.diagnostics ?? { lines: [] }), (e) => setError((e as Error).message));
          }}
        >
          <summary className="small">Diagnostics</summary>
          {diagnostics ? <DiagnosticsView d={diagnostics} userAgent={f.userAgent} /> : <span className="muted small">Loading…</span>}
        </details>
      )}
      {f.comments.length > 0 && (
        <div className="feedback-notes">
          {f.comments.map((c) => (
            <div key={c.id} className="feedback-note">
              <span className="small">
                <strong>{c.author}</strong> <span className="faint">{when(c.createdAt)}</span>
              </span>
              <p className="small">{c.body}</p>
            </div>
          ))}
        </div>
      )}
      <div className="feedback-actions">
        <select className="sm" aria-label="Status" value={f.status} disabled={busy} onChange={(e) => void run(api.updateFeedback(f.id, { status: e.target.value as FeedbackStatus }))}>
          {STATUSES.map(([id, label]) => (
            <option key={id} value={id}>
              {label}
            </option>
          ))}
        </select>
        <select
          className="sm"
          aria-label="Priority"
          value={f.priority ?? ''}
          disabled={busy}
          onChange={(e) => void run(api.updateFeedback(f.id, { priority: (e.target.value || null) as FeedbackPriority | null }))}
        >
          <option value="">No priority</option>
          {PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {p[0].toUpperCase() + p.slice(1)} priority
            </option>
          ))}
        </select>
        <input className="note-input" placeholder="Add a note (Enter to save)" value={note} disabled={busy} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void addNote()} />
        <button className="ghost icon sm danger" title="Delete this feedback" aria-label="Delete this feedback" onClick={() => setConfirming(true)}>
          <Icon name="trash" size={15} />
        </button>
      </div>
      {error && <div className="alert bad small">{error}</div>}
      {confirming && (
        <ConfirmDialog
          title="Delete this feedback?"
          confirmLabel="Delete"
          onClose={() => setConfirming(false)}
          onConfirm={async () => {
            await api.deleteFeedback(f.id);
            onDelete();
          }}
        >
          <p>The message, its diagnostics and your notes on it are removed for good.</p>
        </ConfirmDialog>
      )}
    </section>
  );
}

function DiagnosticsView({ d, userAgent }: { d: FeedbackDiagnostics; userAgent: string | null }) {
  const s = d.studio;
  return (
    <div className="col" style={{ gap: 8, marginTop: 8 }}>
      <div className="feedback-meta">
        {userAgent && <span className="mono">{userAgent}</span>}
        {d.screen && <span>{d.screen}</span>}
      </div>
      {s && (
        <div className="feedback-meta">
          <span>
            {s.name || 'No name'} as {s.role}, participant {s.participantId}
          </span>
          <span>{s.connected ? `connected, ${s.peers} other${s.peers === 1 ? '' : 's'} in the call` : 'not connected'}</span>
          <span>{s.recording ? `recording take ${s.takeId}` : s.takeId ? `last take ${s.takeId}` : 'no take yet'}</span>
          {s.sources.map((x) => (
            <span key={x}>{x}</span>
          ))}
        </div>
      )}
      <pre className="transcript-text">{d.lines.length ? d.lines.join('\n') : 'No log lines.'}</pre>
    </div>
  );
}

/** "Chrome 141 on macOS" from a user agent, roughly. */
function browserOf(ua: string) {
  const os = /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Mac OS X/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : '';
  const m = /Edg\/(\d+)/.exec(ua) ?? /Firefox\/(\d+)/.exec(ua) ?? /Chrome\/(\d+)/.exec(ua) ?? /Version\/(\d+)[.\d]* .*Safari/.exec(ua);
  const name = !m ? 'Browser' : m[0].startsWith('Edg') ? 'Edge' : m[0].startsWith('Firefox') ? 'Firefox' : m[0].startsWith('Chrome') ? 'Chrome' : 'Safari';
  return `${name}${m ? ` ${m[1]}` : ''}${os ? ` on ${os}` : ''}`;
}

function when(ms: number) {
  const s = (Date.now() - ms) / 1000;
  if (s < 90) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}
